/**
 * The Lead Action Center — Cascade's configuration of the shared primitive.
 *
 * It replaces three controls that used to be three separate decisions: the Log
 * Activity modal (type, outcome, verification method, notes, next step, date),
 * the Snooze popover, and the Verified badge that only a modal could set. What
 * a user actually does on a lead is one thought — "here is what happened, come
 * back to me on Tuesday, and yes this one is real" — so it is now one form and
 * one Update.
 *
 * Everything is local state until Update. That is deliberate: this card sits in
 * a queue people scan, and a control that wrote on click would turn reading the
 * list into editing it.
 *
 * The activity type is not asked for. Every update is recorded as a note; the
 * kind of touch a note describes is already in its text, and making someone
 * pick from six values before writing a sentence was the tax the modal charged.
 *
 * Four things a user can do to a lead from here, which is the whole list: write a
 * note (typed or dictated — the mic is the shared ActionCenter's, so a voice note
 * is the same note), set the follow-up date, set the status, and mark it Verified.
 * One Update saves all of it as one interaction. Email drafting stays in the
 * Lead header, where it has a single entry point.
 *
 * Status is a plain four-value select over the canonical lifecycle. It sits beside
 * the two terminal buttons rather than replacing them: the buttons confirm the
 * decision that takes a lead out of the queue, and the select is for the ordinary
 * case of a status that needs correcting — including reopening a lead somebody
 * finished too early. Both write the same field through the same request.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Trash2 } from 'lucide-react';
import {
  ActionCenter,
  ActionCenterCheckbox,
  ActionCenterField,
  ActionCenterTerminalButton,
  FollowUpPicker,
  followUpDateToIso,
  isoToFollowUpDate,
} from '@command-center/ui-system';
import {
  CASCADE_LEAD_STATUSES,
  CASCADE_LEAD_STATUS_LABELS,
  isOpenCascadeStatus,
  toCascadeLeadStatus,
  type CascadeLeadStatus,
  type LccLead,
} from '@command-center/leadloop-core';
import { ApiClientError } from '../lib/api';
import { leadloopApi } from '../lib/leadloop-api';
import { useFollowUpOffsets } from '../lib/portal-settings-api';

export function LeadActionCenter({
  lead,
  recommendation,
  className,
}: {
  lead: LccLead;
  /** What the queue says this lead needs, when it is in the queue. */
  recommendation?: string;
  className?: string;
}) {
  const offsets = useFollowUpOffsets();
  const queryClient = useQueryClient();

  const savedFollowUp = useMemo(() => isoToFollowUpDate(lead.nextFollowUpAt), [lead.nextFollowUpAt]);
  /*
    Normalized, because Airtable stores the display label and history stores
    retired values: a lead whose cell reads "Handed Off" must select as Verified &
    Qualified rather than land the dropdown on nothing.
  */
  const savedStatus = toCascadeLeadStatus(lead.status);
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState(savedFollowUp);
  const [status, setStatus] = useState<CascadeLeadStatus>(savedStatus);
  const [verified, setVerified] = useState(Boolean(lead.verified));
  const [saved, setSaved] = useState<string | null>(null);

  /*
    Keyed on the lead so moving between records in the split view starts a clean
    draft rather than carrying one lead's half-written note onto the next. The
    saved follow-up, status, and Verified reseed from the record for the same
    reason.
  */
  const [seededFor, setSeededFor] = useState(lead.id);
  if (seededFor !== lead.id) {
    setSeededFor(lead.id);
    setNote('');
    setFollowUp(savedFollowUp);
    setStatus(savedStatus);
    setVerified(Boolean(lead.verified));
    setSaved(null);
  }

  const followUpChanged = followUp !== savedFollowUp;
  const verifiedChanged = verified !== Boolean(lead.verified);
  const statusChanged = status !== savedStatus;
  const hasNote = note.trim().length > 0;
  const nothingToSave = !hasNote && !followUpChanged && !verifiedChanged && !statusChanged;
  const finished = !isOpenCascadeStatus(lead.status);

  /*
    One mutation for every way this form can be submitted, including the two
    terminal buttons. Completing a lead while carrying a half-written note must
    save the note with it — the sentence explaining why is the most valuable
    thing on a lead that is leaving the queue, and a separate endpoint would
    have dropped it.
  */
  const update = useMutation({
    /*
      `lifecycle` is the status this submission asks for. The two terminal buttons
      pass theirs directly; a plain Update passes whatever the Status control is
      set to, but only when the user actually changed it — sending the status a
      lead already has would turn every note into a status event.
    */
    mutationFn: (lifecycle?: CascadeLeadStatus) => {
      const nextStatus = lifecycle ?? (statusChanged ? status : undefined);
      return leadloopApi.recordInteraction(lead.id, {
        // One type, always. See the note at the top of this file.
        interactionType: 'internal_note',
        ...(hasNote ? { notes: note.trim() } : {}),
        ...(verifiedChanged ? { verified } : {}),
        ...(followUpChanged ? { nextFollowUpAt: followUpDateToIso(followUp) } : {}),
        ...(nextStatus ? { lifecycle: nextStatus } : {}),
      });
    },
    onSuccess: async (_data, lifecycle) => {
      setNote('');
      setSaved(
        lifecycle === 'verified_qualified'
          ? 'Verified & qualified'
          : lifecycle === 'dismissed'
            ? 'Dismissed'
            : 'Updated',
      );
      window.setTimeout(() => setSaved(null), 2500);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lead', lead.id] }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['loop'] }),
        queryClient.invalidateQueries({ queryKey: ['cascade', 'leadloop'] }),
        queryClient.invalidateQueries({ queryKey: ['activity', lead.id] }),
      ]);
    },
  });

  return (
    <ActionCenter
      heading="Action needed"
      recommendation={recommendation}
      className={className}
      note={note}
      onNoteChange={(value) => {
        setNote(value);
        setSaved(null);
      }}
      notePlaceholder="Add a note about this lead…"
      noteLabel="Note about this lead"
      onSubmit={() => update.mutate(undefined)}
      disabled={nothingToSave}
      pending={update.isPending}
      error={writeError(update.error)}
      success={saved}
      /*
        The two decisions that finish a lead, sitting secondary to Update on
        purpose. The everyday interaction is a note and a date; completing or
        dismissing happens once per lead, at the end, and a control used once
        should not have the same weight as the one used every visit.

        "Verified & Qualified" is written as the human decision it is rather
        than exposed as a status dropdown — it is the moment LeadLoop's work on
        this record is finished, not a step to another pipeline. It carries the
        success tone because it IS the success.

        They disappear once the lead is finished. There is nothing to complete
        twice, and offering Dismiss on a dismissed lead invites a user to test
        what it does.
      */
      actions={
        finished ? undefined : (
          <>
            <ActionCenterTerminalButton
              label="Verified & Qualified"
              confirmLabel="Mark verified & qualified"
              icon={CheckCircle2}
              tone="success"
              disabled={update.isPending}
              onConfirm={() => update.mutate('verified_qualified')}
            />
            <ActionCenterTerminalButton
              label="Dismiss"
              confirmLabel="Dismiss this lead"
              icon={Trash2}
              tone="danger"
              disabled={update.isPending}
              onConfirm={() => update.mutate('dismissed')}
            />
          </>
        )
      }
    >
      {/*
        The date that brings this lead back. Quick offsets and a date input write
        the one field the queue reads (`next_follow_up_at`), so a future date rests
        the lead and its arrival returns it — no second control, no Snoozed status.
      */}
      <ActionCenterField label="Follow up">
        <FollowUpPicker
          value={followUp}
          onChange={(value) => {
            setFollowUp(value);
            setSaved(null);
          }}
          offsets={offsets}
          disabled={update.isPending}
        />
      </ActionCenterField>

      {/*
        Status, as a plain select over the four canonical states and nothing else.

        A native <select> rather than the design-system Select on purpose: this sits
        inside the Action Center's own <form>, next to a native date input and a
        native checkbox, and a portaled listbox in a form is a class of bug this
        codebase has already paid for twice.

        It saves with Update like every other control, not on change — the rule the
        whole panel is built on. The two terminal buttons below are shortcuts that
        write the same field; they stay because finishing a lead is worth a
        confirmation, and this is worth having for the times a status simply needs
        correcting.
      */}
      <ActionCenterField label="Status">
        <select
          aria-label="Lead status"
          value={status}
          disabled={update.isPending}
          onChange={(event) => {
            setStatus(event.target.value as CascadeLeadStatus);
            setSaved(null);
          }}
          className="h-8 rounded-md border border-border bg-card px-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
        >
          {CASCADE_LEAD_STATUSES.map((value) => (
            <option key={value} value={value}>
              {CASCADE_LEAD_STATUS_LABELS[value]}
            </option>
          ))}
        </select>
      </ActionCenterField>

      <ActionCenterCheckbox
        checked={verified}
        onChange={(next) => {
          setVerified(next);
          setSaved(null);
        }}
        disabled={update.isPending}
        label="Verified"
        /*
          Says what checking it means and, just as importantly, what it does
          not. Verified reads like a finish line, and users kept expecting it to
          close the lead — so the description states that handing off is a
          separate, deliberate press.
        */
        description="We believe this is a real opportunity. Reversible, and it does not finish the lead."
      />
    </ActionCenter>
  );
}

function writeError(error: unknown): string | null {
  if (error instanceof ApiClientError && error.code === 'feature_disabled') {
    return 'Write actions are not enabled for this install yet.';
  }
  if (error instanceof Error) return error.message;
  return null;
}
